import { createContext, useContext, useRef, useState, ReactNode } from "react";
import clsx from "clsx";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCloudArrowUp,
  faFileLines,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import CustomButton from "../components/customs/custom-button";
import ModalPortal from "../components/modal-portal";
import axiosInstance from "../utils/axiosInstance";
import { ParseErrors } from "../utils/ParseErrors";
import { fileSize, isLiveVersion } from "../utils/chase-actions.constants";

/**
 * Marking a chase as renewed, with its renewal pack.
 *
 * The documents and the status change go up as ONE multipart PATCH, so a policy
 * is never recorded as renewed without the pack that proves it. Names are typed
 * by the agent — the file name is only the default — and the server puts each
 * file in S3 and stores its key in `loan-documents`.
 */

/** One picked file plus what the agent typed about it. `key` is the row id. */
type RenewDoc = { key: string; file: File; name: string; fee: number | null };

type RenewOptions = {
  chaseId?: number;
  /** Saved first on the server — the uploads land under it. */
  version?: string;
  title?: string;
  desc?: string;
  button?: string;
  onDone?: () => void;
};

type RenewState = RenewOptions & {
  docs: RenewDoc[];
  /** Bytes acknowledged by the browser — drives every bar. */
  loaded: number;
  loading: boolean;
  /** The "this looks like a quote ref" check is open. */
  confirming: boolean;
};

const blank: RenewState = {
  docs: [],
  loaded: 0,
  loading: false,
  confirming: false,
};

const RenewContext = createContext<
  { showRenewModal: (options: RenewOptions) => void } | undefined
>(undefined);

export const useRenewModel = () => useContext(RenewContext)!;

/** Strip the extension so the name field opens with something readable. */
const toName = (fileName: string) => fileName.replace(/\.[^.]+$/, "");

const clamp = (n: number) => Math.max(0, Math.min(100, n));

export const RenewModelProvider = ({ children }: { children: ReactNode }) => {
  const [visible, setVisible] = useState(false);
  const [value, setValue] = useState<RenewState>(blank);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { docs, loading, loaded, confirming } = value;

  const set = (patch: Partial<RenewState>) =>
    setValue((s) => ({ ...s, ...patch }));

  const showRenewModal = (options: RenewOptions) => {
    setValue({ ...blank, ...options });
    setVisible(true);
  };

  const hide = () => {
    if (loading) return;
    setVisible(false);
    setValue(blank);
  };

  // ─── The picked files ───────────────────────────────────────────────────────
  const addFiles = (files: FileList | null) =>
    files?.length &&
    set({
      docs: [
        ...docs,
        ...Array.from(files).map((file, i) => ({
          key: `${Date.now()}-${i}-${file.name}`,
          file,
          name: toName(file.name),
          fee: null,
        })),
      ],
    });

  const setDoc = (key: string, patch: Partial<RenewDoc>) =>
    set({ docs: docs.map((d) => (d.key === key ? { ...d, ...patch } : d)) });

  const removeDoc = (key: string) =>
    set({ docs: docs.filter((d) => d.key !== key) });

  // ─── Progress ───────────────────────────────────────────────────────────────
  // One request carries the lot, so the browser only reports total bytes sent.
  // Each row's share is that total minus everything queued ahead of it — near
  // enough, the multipart envelope adds a little on top of the file sizes.
  const total = docs.reduce((sum, d) => sum + d.file.size, 0);
  const overall = total ? clamp((loaded / total) * 100) : 0;
  const rowProgress = (index: number) => {
    const before = docs
      .slice(0, index)
      .reduce((sum, d) => sum + d.file.size, 0);
    return clamp(((loaded - before) / docs[index].file.size) * 100);
  };

  // ─── Submit — version, documents and the status change in one request ───────
  // Anything not starting with 1 isn't a live ref, so it gets a second look.
  const save = () => {
    if (!value.version?.trim())
      return toast.error("Please enter the version reference number");
    if (docs.some((d) => !d.name.trim()))
      return toast.error("Every document needs a name");
    if (isLiveVersion(value.version)) return renew();
    set({ confirming: true });
  };

  const renew = async () => {
    set({ loading: true, loaded: 0, confirming: false });
    const form = new FormData();
    form.append("versionReferenceNumber", value.version!.trim());
    // Appended in row order — the server pairs `documents[i]` with `files[i]`.
    docs.forEach((d) => form.append("files", d.file));
    form.append(
      "documents",
      JSON.stringify(docs.map((d) => ({ name: d.name.trim(), fee: d.fee })))
    );

    try {
      await axiosInstance.patch(`/chase/${value.chaseId}/renewed`, form, {
        onUploadProgress: (event) => set({ loaded: event.loaded }),
      });
      toast.success("Chase marked as renewed");
      value.onDone?.();
      setVisible(false);
      setValue(blank);
    } catch (error) {
      toast.error(ParseErrors(error));
      set({ loading: false, loaded: 0 });
    }
  };

  return (
    <RenewContext.Provider value={{ showRenewModal }}>
      {children}
      {visible && (
        <ModalPortal>
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center backdrop-blur-md bg-black/50 animate-fade-in p-4"
            onClick={hide}
          >
            <div
              className="bg-surface-elevated border border-edge p-6 rounded-2xl shadow-overlay w-full max-w-[620px] max-h-[90vh] overflow-y-auto custom-scrollbar animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-semibold text-text-primary">
                {value.title ?? "Mark this chase as renewed?"}
              </h2>
              <p className="mt-2 text-light-gray">
                {value.desc ??
                  "The policy is recorded as renewed and the chase stops."}
              </p>

              {/* ─── Version reference — saved before anything is uploaded ─── */}
              <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3.5 py-3 text-[13px] text-amber-600">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="mt-0.5 shrink-0"
                />
                <span>
                  Make sure this is the <b>live</b> reference number, not the
                  quote reference number.
                </span>
              </div>
              <label className="mt-3 text-label-muted font-medium block text-sm mb-1">
                Version Reference Number *
              </label>
              <input
                value={value.version ?? ""}
                disabled={loading}
                placeholder="Live version reference, e.g. 1…"
                onChange={(e) => set({ version: e.target.value })}
                className="border border-input-border rounded-lg bg-input-bg text-text-input text-sm px-3 h-[40px] w-full outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30 disabled:opacity-60"
              />

              {/* ─── Drop zone ─────────────────────────────────────────────── */}
              <p className="mt-5 mb-2 flex items-center gap-2 text-[11px] font-semibold text-light-gray uppercase tracking-[0.14em]">
                <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                Renewal Documents
              </p>
              <button
                type="button"
                disabled={loading}
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  if (!loading) addFiles(e.dataTransfer.files);
                }}
                className={clsx(
                  "w-full rounded-xl border-2 border-dashed px-4 py-7 flex flex-col items-center gap-2 transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed",
                  dragging
                    ? "border-accent bg-active-background"
                    : "border-edge-strong bg-surface-muted/60 hover:border-indigo-400/50 hover:bg-active-background"
                )}
              >
                <span className="w-11 h-11 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-accent to-accent-bright shadow-sm shadow-indigo-500/30">
                  <FontAwesomeIcon icon={faCloudArrowUp} />
                </span>
                <span className="text-[14px] font-semibold text-heading-color">
                  Drop the renewal pack here, or{" "}
                  <span className="text-blue">browse</span>
                </span>
                <span className="text-[12px] text-light-gray">
                  Any file type, up to 25 MB each
                </span>
              </button>
              <input
                ref={fileInput}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  addFiles(e.target.files);
                  // Cleared so re-picking the same file still fires onChange.
                  e.target.value = "";
                }}
              />

              {/* ─── One row per file — name it, price it, drop it ──────────── */}
              {docs.length > 0 && (
                <div className="mt-3 space-y-2">
                  {docs.map((doc, index) => {
                    const percent = loading ? rowProgress(index) : 0;
                    const done = percent >= 100;
                    return (
                      <div
                        key={doc.key}
                        className="rounded-xl border border-edge bg-surface-muted/60 p-3"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={clsx(
                              "w-9 h-9 shrink-0 rounded-lg flex items-center justify-center border transition-colors duration-300",
                              done
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-500"
                                : "border-edge bg-surface-elevated text-light-gray"
                            )}
                          >
                            <FontAwesomeIcon
                              icon={done ? faCircleCheck : faFileLines}
                              className="text-sm"
                            />
                          </span>

                          <div className="flex-1 min-w-0">
                            <input
                              value={doc.name}
                              disabled={loading}
                              placeholder="Document name, e.g. TOBA"
                              onChange={(e) =>
                                setDoc(doc.key, { name: e.target.value })
                              }
                              className="w-full bg-transparent text-[14px] font-medium text-heading-color outline-none border-b border-transparent focus:border-indigo-400/50 transition-colors duration-200 disabled:opacity-60"
                            />
                            <p className="mt-0.5 text-[11px] text-light-gray truncate">
                              {doc.file.name} · {fileSize(doc.file.size)}
                            </p>
                          </div>

                          <div className="relative w-[92px] shrink-0">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-light-gray text-[13px]">
                              £
                            </span>
                            <input
                              type="number"
                              placeholder="Fee"
                              disabled={loading}
                              value={doc.fee ?? ""}
                              onChange={(e) =>
                                setDoc(doc.key, {
                                  fee:
                                    e.target.value === ""
                                      ? null
                                      : Number(e.target.value),
                                })
                              }
                              className="border border-input-border rounded-lg bg-input-bg text-text-input text-[13px] tabular-nums pl-5 pr-2 h-[34px] w-full outline-none transition-colors duration-200 focus:border-indigo-400/50 focus:ring-1 focus:ring-indigo-500/30 disabled:opacity-60"
                            />
                          </div>

                          <button
                            type="button"
                            disabled={loading}
                            onClick={() => removeDoc(doc.key)}
                            className="w-8 h-8 shrink-0 rounded-lg text-light-gray hover:text-red-400 hover:bg-red-500/10 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="text-xs"
                            />
                          </button>
                        </div>

                        {/* This file's share of the bytes sent so far. */}
                        {loading && (
                          <div className="mt-2.5 h-1 w-full rounded-full bg-overlay overflow-hidden">
                            <div
                              className={clsx(
                                "h-full rounded-full transition-[width] duration-200 ease-out",
                                done
                                  ? "bg-emerald-500"
                                  : "bg-gradient-to-r from-accent to-accent-bright"
                              )}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ─── Overall bar — the whole pack, one request ──────────────── */}
              {loading && total > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[12px] font-semibold">
                    <span className="text-light-gray">
                      {overall >= 100
                        ? "Finishing up…"
                        : `Uploading ${docs.length} document${docs.length > 1 ? "s" : ""}`}
                    </span>
                    <span className="text-heading-color tabular-nums">
                      {Math.round(overall)}%
                    </span>
                  </div>
                  <div className="mt-1.5 h-2 w-full rounded-full bg-overlay overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-accent to-accent-bright transition-[width] duration-200 ease-out"
                      style={{ width: `${overall}%` }}
                    />
                  </div>
                  <p className="mt-1 text-[11px] text-light-gray tabular-nums">
                    {fileSize(Math.min(loaded, total))} of {fileSize(total)}
                  </p>
                </div>
              )}

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <CustomButton
                  disabled={loading}
                  onClick={hide}
                  className="order-2 sm:order-1 border border-edge-strong text-text-primary px-6 py-2 rounded-lg hover:bg-overlay transition cursor-pointer h-[46px] text-md font-semibold"
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  disabled={loading}
                  loading={loading}
                  onClick={save}
                  className="order-1 sm:order-2 text-white px-6 py-2 rounded-lg bg-accent hover:bg-accent-bright transition cursor-pointer h-[46px] text-md font-semibold"
                >
                  {value.button ?? "Mark as renewed"}
                </CustomButton>
              </div>
            </div>

            {/* ─── Not a live ref — confirm before it's saved ──────────────── */}
            {confirming && (
              <div
                className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/40 p-4 animate-fade-in"
                onClick={(e) => {
                  e.stopPropagation();
                  set({ confirming: false });
                }}
              >
                <div
                  className="bg-surface-elevated border border-edge p-6 rounded-2xl shadow-overlay w-full max-w-[420px] animate-fade-in"
                  onClick={(e) => e.stopPropagation()}
                >
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
                    <FontAwesomeIcon
                      icon={faTriangleExclamation}
                      className="text-amber-500"
                    />
                    Are you sure?
                  </h2>
                  <p className="mt-2 text-light-gray">
                    <b className="text-heading-color">{value.version?.trim()}</b>{" "}
                    looks like a quote reference, not a live reference. Live
                    references start with 1.
                  </p>
                  <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                    <CustomButton
                      onClick={() => set({ confirming: false })}
                      className="order-2 sm:order-1 border border-edge-strong text-text-primary px-6 py-2 rounded-lg hover:bg-overlay transition cursor-pointer h-[46px] text-md font-semibold"
                    >
                      Go back
                    </CustomButton>
                    <CustomButton
                      onClick={renew}
                      className="order-1 sm:order-2 text-white px-6 py-2 rounded-lg bg-accent hover:bg-accent-bright transition cursor-pointer h-[46px] text-md font-semibold"
                    >
                      Yes, it's live
                    </CustomButton>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ModalPortal>
      )}
    </RenewContext.Provider>
  );
};
